import Link from 'next/link';
import { handleFreeReturn } from '@lambda/commons/helpers';
import { ProductT } from '@lambda/apis/src/catalog-v2/types';
import settings from '@/settings';
import i18n from '@/settings/i18n';
import { useCollectionPageContext } from '../hooks/useCollectionPageContext';
import seo from '@/settings/seo';
import { interpolateStoreTokens } from '@/lib/store-tokens';

const t = i18n.init('collections');
const tCategory = i18n.init('categories');

const isUSStore = settings.store === 'reebelo-us';

const freeReturnDays = handleFreeReturn(
  settings.store,
  settings.free_return_days,
);

function parseHTML(htmlString: string) {
  let formattedHtmlString = interpolateStoreTokens(htmlString)
    .replace(/<h2/g, `<h2 class="text-xl font-bold mt-8 first:mt-0"`)
    .replace(/<h3/g, `<h3 class="font-bold mt-5"`)
    .replace(/<p/g, `<p class="text-sm mt-1"`)
    .replace(/<li/g, `<li class="text-sm mt-5"`)
    .replace(/<a/g, `<a class="underline text-blue-500"`)
    .replace(/Reebelo/g, `<a href="/">Reebelo</a>`);

  if (settings.store === 'quista') {
    formattedHtmlString = formattedHtmlString.replace(
      /12\s*-?\s*months?/gi,
      'up to 12 months',
    );
    formattedHtmlString = formattedHtmlString.replace(
      /a up to 12 months warranty/gi,
      'an up to 12 months warranty',
    );
  }

  return formattedHtmlString;
}

const SeoSection = () => {
  const {
    products,
    handle,
    collectionName: rawCollectionName,
    type,
  } = useCollectionPageContext();

  // Localize the category name before interpolating it into the on-page SEO
  // copy, so FR/DE renders "Ordinateurs portables …" rather than the English
  // "Laptops". Non-category values (brand handles) pass through unchanged.
  const collectionName = tCategory(`${rawCollectionName}`);

  if (handle && seo[handle]) {
    return (
      <section className="bg-[#f2f2f2] py-10">
        <div className="xl:reebelo-container mx-auto px-5 xl:px-8">
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              // Not wrapped in t(): the seo HTML contains {{return}}/{{warranty}}
              // placeholders (handled by parseHTML), which t()'s interpolation
              // guard rejects. Per-handle SEO copy is localized via the seo
              // settings data, not the i18n string table.
              __html: parseHTML(seo[handle]),
            }}
          />
        </div>
      </section>
    );
  }

  if (type === 'MS_CATALOG_DEALS') {
    return (
      <section className="bg-[#f2f2f2] py-10">
        <div className="xl:reebelo-container mx-auto px-5 xl:px-8">
          <h2 className="text-base font-semibold leading-tight text-gray-700  xxs:text-lg xs:text-xl md:text-xl">
            {isUSStore
              ? t`Buy Certified Refurbished Tech at Amazing Prices in USA`
              : t`Get Amazing Deals for Certified Refurbished Devices in ${{
                  country: settings.country,
                }}`}
          </h2>
          <div className="mt-2 text-sm text-gray-700">
            <p>
              {isUSStore
                ? t`Looking for affordable tech? Find hot deals for certified refurbished devices like Apple MacBook 2015, iPad Mini 1, Samsung Galaxy S21, AirPods, Apple Watch Series 4, and much more. All preloved devices on `
                : t`Sustainable and 100% certified refurbished tech now at affordable prices. For up to 70% off, find limited-time hot deals for your favorite iPhones, Samsung Galaxy S21, iPads, AirPods, and MacBook Pros. Every pre-loved device at `}
              <Link href="/" className="font-semibold text-blue-500">
                Reebelo
              </Link>
              {isUSStore
                ? t` are 100% functional and sourced from carefully vetted local US vendors. We do our due diligence with 40+ point quality checks so you can rest assured and enjoy free delivery and up to 12 months of warranty on a wide selection of second-hand devices. With a quick claims process and ${{
                    freeReturnDays,
                  }}-day returns, you can shop with complete peace of mind for devices that empower you. Check back for more amazing deals and special bundle offers!`
                : t` goes through a thorough 40+ point quality check process. We ensure your purchase is fully functional, sanitized, and safely packaged so you can enjoy it to the fullest. Enjoy up to 12 months warranty, a ${{
                    freeReturnDays,
                  }}-day return policy & free delivery. Check back for more amazing deals and special bundle offers!`}
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (collectionName) {
    const links = products.slice(0, 28).map((_product) => {
      const product = _product as ProductT;

      const productId = product.slug;
      const productName = product.name;

      return (
        <Link
          href={`/collections/${productId}`}
          key={productId}
          className="group text-blue-500 hover:underline"
        >
          {productName}
          <span className="group-last:hidden">, </span>
        </Link>
      );
    });

    return (
      <section className="bg-[#f2f2f2] py-10">
        <div className="xl:reebelo-container mx-auto px-5 xl:px-8">
          <h2 className="text-base font-semibold leading-tight text-gray-700  xxs:text-lg xs:text-xl md:text-xl">
            {isUSStore
              ? t`Buy Refurbished ${{ collectionName }} in the United States`
              : t`Buy Refurbished ${{ collectionName }} in ${{
                  country: settings.country,
                }}`}
          </h2>
          <div className="mt-2 text-sm text-gray-700">
            <p>
              {t`Looking to buy refurbished ${{
                collectionName,
              }}? You're at the right place! Whether you are looking for the latest ${{
                collectionName,
              }} or one of the previous models, we've got you covered. Save big by getting a second hand ${{
                collectionName,
              }} such as `}
              {links}
              {t` at `}
              <Link href="/" className="text-blue-500">
                Reebelo
              </Link>
              {t`. All our ${{ collectionName }} come with up to ${{
                warranty: settings.warranty_month,
              }} months warranty, ${{
                freeReturnDays,
              }} days free returns and free shipping.`}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return null;
};

export default SeoSection;
